import clsx from 'clsx';
import { isEmpty, toNumber } from 'lodash';
import { func, shape, string } from 'prop-types';
import { useEffect, useMemo, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { FormInputCount, FormSelect, Icon, Skeleton, Tooltip, useTravelPurchaseContext } from 'components';
import { convertDateString, formatDate } from 'helpers';
import { useClickOutside, useSafeState } from 'hooks';

import 'assets/styles/components/travel-purchase/travel-selection-block.scss';

const TravelSelectionBlock = ({ travelDepartureParams, setTravelDepartureParams }) => {
  const { t } = useTranslation();
  const [blockOpen, setBlockOpen] = useSafeState(false);
  const [showParticipantsOptions, setShowParticipantsOptions] = useSafeState(false);
  const [totalPassengers, setTotalPassengers] = useSafeState(t('purchase:adultPassenger'));

  const participantsRef = useRef(null);
  const { control, setValue, getValues } = useFormContext();
  const { travel, travelDeparture, travelDepartureLoading, getTravelDeparture } = useTravelPurchaseContext();
  const storedPassengers = useSelector((state) => state.travelPurchase.passengersCat);
  const storedDepartureDate = useSelector((state) => state.travelPurchase.departureDate);
  const storedDepartureCity = useSelector((state) => state.travelPurchase.departureCity);
  const storedDuration = useSelector((state) => state.travelPurchase.duration);

  /**
   * Options for departure cities select
   */
  const fromOptions = useMemo(
    () => travelDeparture?.froms?.map((from) => ({ value: from.id, label: from.name })) || [],
    [travelDeparture]
  );

  /**
   * Options for duration select
   */
  const durationOptions = useMemo(
    () => travelDeparture?.durations?.map((duration) => ({ value: duration.id, label: duration.name })) || [],
    [travelDeparture]
  );

  /**
   * Options for departure dates (month and year) select
   */
  const departurePeriodOptions = useMemo(() => {
    return (
      travelDeparture?.periods?.map((period) => ({ value: convertDateString(period.id), label: period.name })) || []
    );
  }, [travelDeparture]);

  // -----------------------------------------------------------------------------------------------------
  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  /* Prefill form with default values (API response) or selected values stored in redux */
  useEffect(() => {
    if (travelDeparture) {
      initFormValues();
      initPassengersValues();
    }
  }, [travelDeparture]);

  useEffect(() => {
    if (storedDepartureDate) setValue('selection.selectedTravel', storedDepartureDate);
  }, [storedDepartureDate]);

  // -----------------------------------------------------------------------------------------------------
  // Component Methods
  // -----------------------------------------------------------------------------------------------------
  const initPassengersValues = () => {
    /**
     * Set default values for each passenger or use stored values if available
     * Adult passenger is set to 1 by default
     * Other passengers are set to 0 by default
     */
    let passengers = storedPassengers || [];

    if (isEmpty(passengers)) {
      passengers =
        travelDeparture.passengersCat?.map((passenger) => ({
          ...passenger,
          qty: passenger.type === 'adult' ? 1 : 0
        })) || [];
    }

    // Set passenger with type 'adult' in first position
    const adultIndex = passengers?.findIndex((passenger) => passenger.type === 'adult');
    if (adultIndex !== -1 && adultIndex !== 0) {
      const adultPassenger = passengers[adultIndex];
      passengers.splice(adultIndex, 1);
      passengers.unshift(adultPassenger);
    }

    setValue('selection.passengers', passengers);
    setTotalPassengersLabel();
  };

  const initFormValues = () => {
    const setFormValue = (options, name, storedValue = null) => {
      // Default select value is the first option
      let value = options[0];

      // If value has been changed using select, update form with selected value
      if (travelDepartureParams?.[name]) {
        const option = options.find((opt) => opt.value === travelDepartureParams[name]);
        if (option) value = option;
      } else if (storedValue) {
        // On page load, set form value with stored value if available
        const option = options.find((opt) => opt.value === storedValue);
        if (option) value = option;
      }
      setValue(`selection.${name}`, value);
    };

    if (fromOptions.length > 0) {
      setFormValue(fromOptions, 'from', storedDepartureCity?.value);
    }
    if (durationOptions.length > 0) {
      setFormValue(durationOptions, 'duration', storedDuration?.value);
    }
    if (departurePeriodOptions.length > 0) {
      const monthYear = formatDate(storedDepartureDate, 'MM-yyyy');
      const storedMonthYear = departurePeriodOptions.find((option) => option.value === monthYear)?.value;
      setFormValue(departurePeriodOptions, 'monthYear', storedMonthYear || null);
    }
  };

  const passentTooltipContent = (passenger) => {
    const maxOld = toNumber(passenger.maxOld);
    const minOld = toNumber(passenger.minOld);

    if (minOld && maxOld) {
      return t('purchase:minMaxAge', { minAge: minOld, maxAge: maxOld - 1 });
    } else if (minOld && !maxOld) {
      return t('purchase:minAge', { minAge: minOld });
    } else if (!minOld && maxOld) {
      return t('purchase:maxAge', { maxAge: maxOld });
    }
    return '';
  };

  const setTotalPassengersLabel = () => {
    const { passengers } = getValues('selection');
    // Set the first adult passenger to 1 if no adult passenger is selected
    const adultPassenger = passengers.find((passenger) => passenger.type === 'adult');
    setValue('selection.passengers.0.value', adultPassenger.qty <= 0 ? 1 : adultPassenger.qty);

    let total = '';

    passengers.forEach((passenger) => {
      if (passenger.qty > 0) {
        total += `${passenger.qty} ${passenger.label}${passenger.qty > 1 ? 's' : ''}, `;
      }
    });

    // Remove last char (',')
    total = total.slice(0, -2);

    setTotalPassengers(total);
  };

  const onCloseParticipantsDropdown = () => {
    // Close dropdown
    setShowParticipantsOptions(false);
    setTotalPassengersLabel();
  };

  // Update form values on close mobile block
  const handleMobileValues = () => {
    setTotalPassengersLabel();
    setBlockOpen(false);
  };

  const handleSelectChange = (selectOption, inputName) => {
    const formSelectionValues = getValues('selection');
    // Update form value on select change
    setValue(`selection.${inputName}`, selectOption, { shouldValidate: true });

    // Update travel departure params
    const updatedParams = {
      // Default values from form
      from: formSelectionValues?.from?.value,
      duration: formSelectionValues?.duration?.value,
      monthYear: formSelectionValues?.monthYear?.value,
      ...travelDepartureParams,
      // New selected value
      [inputName]: selectOption.value
    };

    setTravelDepartureParams(updatedParams);

    // Call API to get travel departure data depending on selected options
    getTravelDeparture(updatedParams);
  };

  useClickOutside(participantsRef, () => {
    showParticipantsOptions && onCloseParticipantsDropdown();
  }, [showParticipantsOptions]);

  return (
    <div className="mb-4 lg:mb-2">
      {/* SKELETON */}
      {travelDepartureLoading && (
        <>
          <Skeleton classNames="w-full h-24 hidden lg:block" />

          <div className="flex-center-between lg:hidden">
            <Skeleton classNames="h-6 w-24" />
            <Skeleton classNames="h-6 w-24" />
          </div>
          <Skeleton classNames="h-6 w-44 mb-4 lg:hidden" />
          <Skeleton classNames="h-6 w-44 mb-4 lg:hidden" />
          <Skeleton classNames="h-6 w-44 lg:hidden" />
        </>
      )}

      {!travelDepartureLoading && !isEmpty(travelDeparture) && (
        <div>
          {/* MOBILE ONLY CONTENT - Travel selection resume */}
          <div className="lg:hidden">
            <div className={clsx('overlay', blockOpen && 'open')} onClick={() => setBlockOpen(false)} />
            <div className="flex-center-between mb-4">
              <p className="uppercase">{t('purchase:criteria')}</p>

              <button type="button" className="uppercase text-primary-500" onClick={() => setBlockOpen(true)}>
                {t('buttons.modify')}
              </button>
            </div>
            <div className="flex-center-start gap-2 mb-4">
              <Icon name="location-dot" iconStyle="fad" classes="text-yellow-500" />
              {travel?.backCountry && travel?.backCity && (
                <span className="uppercase">
                  {travel?.backCountry} - {travel?.backCity}
                </span>
              )}
            </div>

            <div className="flex-center-start gap-2 mb-4">
              <Icon name="plane-departure" iconStyle="fad" classes="text-yellow-500" />
              <span>{getValues('selection.from')?.label}</span>
            </div>

            <div className="flex-center-start gap-2 mb-4">
              <Icon name="user-group" iconStyle="fad" classes="text-yellow-500" />
              <span>{totalPassengers}</span>
            </div>
          </div>

          <form className={clsx('travel-selection-form', blockOpen && 'open')}>
            {/* DESTINATION OPTIONS */}
            <Controller
              render={() => (
                <FormSelect
                  options={fromOptions}
                  onChange={(option) => handleSelectChange(option, 'from')}
                  control={control}
                  name="selection.from"
                  className="lg:shrink-0"
                  placeholder={t('form:placeholders.select')}
                  label={t('form:departureCity')}
                  icon={{ name: 'plane-departure', class: 'text-yellow-500' }}
                  isSearchable={false}
                  disabled={fromOptions?.[0]?.label === 'Sans Transport' || fromOptions?.length === 1}
                />
              )}
              name="selection.from"
              rules={{ required: true }}
            />

            {/* DURATION OPTIONS */}
            <Controller
              render={() => (
                <FormSelect
                  options={durationOptions}
                  onChange={(option) => handleSelectChange(option, 'duration')}
                  control={control}
                  name="selection.duration"
                  className="lg:shrink-0"
                  placeholder={t('form:placeholders.select')}
                  label={t('form:durationLong')}
                  icon={{ name: 'calendar-days', style: 'fad', class: 'text-yellow-500' }}
                  isSearchable={false}
                  disabled={durationOptions?.length === 1}
                />
              )}
              name="selection.duration"
              rules={{ required: true }}
            />

            {/* DEPARTURE OPTIONS */}
            <Controller
              render={() => (
                <FormSelect
                  options={departurePeriodOptions}
                  onChange={(option) => handleSelectChange(option, 'monthYear')}
                  control={control}
                  name="selection.monthYear"
                  placeholder={t('form:placeholders.select')}
                  className="lg:shrink-0"
                  label={t('form:departurePeriod')}
                  icon={{ name: 'calendar-days', style: 'fad', class: 'text-yellow-500' }}
                  isSearchable={false}
                  disabled={departurePeriodOptions?.length === 1}
                />
              )}
              name="selection.monthYear"
              rules={{ required: true }}
            />

            {/* PARTICIPANTS OPTIONS */}
            <div className="grow relative" ref={participantsRef}>
              <div className="text-gray-700 uppercase lg:text-sm lg:font-medium">{t('form:participants')}</div>

              <button
                type="button"
                className={clsx(
                  'participants-dropdown-btn flex-center-between gap-4 min-h-[2rem] w-full',
                  showParticipantsOptions && 'expanded'
                )}
                onClick={() => setShowParticipantsOptions(true)}
              >
                <div className="text-left lowercase truncate-line two-lines">{totalPassengers}</div>
                <Icon name="chevron-down" />
              </button>

              <div className={clsx('participants-dropdown-list', showParticipantsOptions && 'expanded')}>
                {getValues('selection.passengers')?.map((passenger, i) => (
                  <div key={`passenger-cat-${i}`} className="flex items-center gap-4 mb-3 last:mb-0">
                    <FormInputCount
                      name={`selection.passengers.${i}.qty`}
                      max={9}
                      min={passenger.type === 'adult' ? 1 : 0}
                      rules={{ min: passenger.type === 'adult' ? 1 : 0, max: 9 }}
                    />

                    <div className="text-gray-700">
                      {passenger.label}
                      <Tooltip
                        className="pl-2"
                        id={`passenger-tooltip-${i}`}
                        content={passentTooltipContent(passenger)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* SUBMIT BUTTON FOR MOBILE */}
            <button type="button" className="btn secondary mt-8 lg:hidden" onClick={handleMobileValues}>
              {t('purchase:configureTravel')}
            </button>
          </form>
        </div>
      )}
    </div>
  );
};

TravelSelectionBlock.propTypes = {
  travelDepartureParams: shape({
    monthYear: string,
    from: string,
    duration: string
  }),
  setTravelDepartureParams: func.isRequired
};

export default TravelSelectionBlock;
